import React, { Component } from "react";
import { api, fmt } from "../utils/api";
import toast from "react-hot-toast";
import {
  TrendingUp, TrendingDown, Hotel, IndianRupee, RefreshCw,
  MapPin, FileText, PlaneTakeoff, PlaneLanding,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// LIVE GP HELPERS — TAC deducted from purchase, extras included, no stale DB values
// (unchanged — business logic, do not touch)
// ─────────────────────────────────────────────────────────────────────────────

function computeLiveGP(b) {
  const rtb    = b.room_type_bookings || [];
  const nights = parseInt(b.total_nights || b.no_of_nights) || 1;
  if (rtb.length === 0) return parseFloat(b.gross_profit) || 0;
  const isSC = b.mode === "Service Charge";
  let saleBase = 0, purBase = 0, tacTotal = 0, saleGst = 0, purGst = 0;
  for (const r of rtb) {
    const qty       = parseInt(r.room_count || r.count) || 0;
    const tacRoom   = parseFloat(r.tac_on_room)    || 0;
    const tacEB     = parseFloat(r.tac_on_ex_bed)  || 0;
    const ebc       = parseInt(r.extra_bed_child)  || 0;
    const eba       = parseInt(r.extra_bed_adult)  || 0;
    const roomSale  = qty * nights * (parseFloat(r.sale_price) || 0);
    const roomPur   = qty * nights * (parseFloat(r.purchase_price) || 0);  // gross
    const ebcPur    = ebc * nights * (parseFloat(r.ex_bed_rate_child_purchase) || 0);
    const ebaPur    = eba * nights * (parseFloat(r.ex_bed_rate_purchase) || 0);  // gross
    const ebSale    = ebc * nights * (parseFloat(r.ex_bed_rate_child_sale)     || 0)
                     + eba * nights * (parseFloat(r.ex_bed_rate_sale)           || 0);
    saleBase += roomSale + ebSale + (parseFloat(r.extras_amount_sale)     || 0);
    purBase  += roomPur  + ebcPur + ebaPur + (parseFloat(r.extras_amount_purchase) || 0);
    tacTotal += tacRoom + tacEB;  // flat, single — not multiplied by qty/nights
    saleGst  += (roomSale + ebSale) * ((parseFloat(r.gst_rate) || 0) / 100);
    purGst   += (roomPur + ebcPur + ebaPur) * ((parseFloat(r.purchase_gst_rate) || 0) / 100);
  }
  if (isSC) {
    const saleTotal = saleBase + saleGst;
    const purTotal  = Math.max(0, purBase + purGst - tacTotal);
    return Math.round((saleTotal - purTotal) * 100) / 100;
  }
  const gpBase = saleBase - purBase;
  return Math.round((gpBase < 0 ? gpBase + tacTotal : gpBase - tacTotal) * 100) / 100;
}

function getEffectiveSale(b) {
  const top = parseFloat(b.total_sale) || 0;
  if (top > 0) return top;
  const rtb    = b.room_type_bookings || [];
  const nights = parseInt(b.total_nights || b.no_of_nights) || 1;
  return rtb.reduce((sum, r) => {
    const qty   = parseInt(r.room_count || r.count) || 0;
    const gst   = parseFloat(r.gst_rate) || 0;
    const room  = qty * nights * (parseFloat(r.sale_price) || 0);
    const ebcS  = (parseInt(r.extra_bed_child) || 0) * nights * (parseFloat(r.ex_bed_rate_child_sale) || 0);
    const ebaS  = (parseInt(r.extra_bed_adult) || 0) * nights * (parseFloat(r.ex_bed_rate_sale)       || 0);
    const ext   = parseFloat(r.extras_amount_sale) || 0;
    return sum + (room + ebcS + ebaS) * (1 + gst / 100) + ext;
  }, 0);
}

function getEffectivePurchase(b) {
  const top = parseFloat(b.total_purchase) || 0;
  if (top > 0) return top;
  const rtb    = b.room_type_bookings || [];
  const nights = parseInt(b.total_nights || b.no_of_nights) || 1;
  return rtb.reduce((sum, r) => {
    const qty       = parseInt(r.room_count || r.count) || 0;
    const tacRoom   = parseFloat(r.tac_on_room)   || 0;
    const tacEB     = parseFloat(r.tac_on_ex_bed)  || 0;
    const purGst    = parseFloat(r.purchase_gst_rate || r.gst_rate) || 0;
    const room  = qty * nights * (parseFloat(r.purchase_price) || 0);  // gross
    const ebcP  = (parseInt(r.extra_bed_child) || 0) * nights * (parseFloat(r.ex_bed_rate_child_purchase) || 0);
    const ebaP  = (parseInt(r.extra_bed_adult) || 0) * nights * (parseFloat(r.ex_bed_rate_purchase) || 0);  // gross
    const ext   = parseFloat(r.extras_amount_purchase) || 0;
    const grossPlusGst = (room + ebcP + ebaP) * (1 + purGst / 100);
    return sum + Math.max(0, grossPlusGst - tacRoom - tacEB) + ext;  // TAC: flat, once, after GST
  }, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// Ink anchors to the existing KTS brand navy (#1E2D4F) for continuity with the
// rest of the app. Coral is the new "trip/energy" accent — an OTA-adjacent
// warmth without lifting any single competitor's exact brand red. Sora carries
// headings/numbers, JetBrains Mono carries money & confirmation codes (both
// already used elsewhere in this codebase).
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  ink:       "#16213E",
  inkSoft:   "#6B7280",
  surface:   "#F4F5FA",
  card:      "#FFFFFF",
  line:      "#E7E9F2",
  coral:     "#FF6B4A",
  coralSoft: "#FFEDE7",
  gold:      "#C9A84C",
  green:     "#0FA968",
  greenSoft: "#E4F9EE",
  red:       "#E1483A",
  redSoft:   "#FCEAE8",
  blue:      "#2F5FE0",
  blueSoft:  "#EAEFFE",
  amber:     "#B7791F",
  amberSoft: "#FFF6E0",
  fontDisplay: "'Sora', sans-serif",
  fontMono:    "'JetBrains Mono', monospace",
};

const statusColors = {
  Draft:              { bg: "#F3F4F6", c: "#4B5563", b: "#E5E7EB" },
  "Pending Approval":  { bg: T.amberSoft, c: T.amber, b: "#FDE9B8" },
  Approved:           { bg: T.blueSoft,  c: T.blue,  b: "#D3DEFC" },
  Invoiced:           { bg: T.greenSoft, c: T.green, b: "#BEEED9" },
  Rejected:           { bg: T.redSoft,   c: T.red,   b: "#F5CAC5" },
};

function ConfBadge({ confNo }) {
  return confNo
    ? <span style={{ fontFamily: T.fontMono, fontSize: 11, fontWeight: 700, color: T.green, background: T.greenSoft, padding: "2px 8px", borderRadius: 6, border: "1px solid #BEEED9" }}>{confNo}</span>
    : <span style={{ fontSize: 11, color: T.amber, background: T.amberSoft, padding: "2px 8px", borderRadius: 6, border: "1px solid #FDE9B8" }}>⏳ Awaiting</span>;
}

function StatusPill({ status }) {
  const m = statusColors[status] || statusColors.Draft;
  return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: m.bg, color: m.c, border: `1px solid ${m.b}`, whiteSpace: "nowrap" }}>{status}</span>;
}

// ── Hero metric — the one number ops cares about most, given real weight ────
function HeroStat({ value, sub, positive }) {
  const color = positive ? T.green : T.red;
  const soft  = positive ? T.greenSoft : T.redSoft;
  return (
    <div style={{ background: T.ink, borderRadius: 20, padding: "26px 28px", flex: "1 1 300px", position: "relative", overflow: "hidden", minWidth: 280 }}>
      <div style={{ position: "absolute", top: -60, right: -60, width: 180, height: 180, borderRadius: "50%", background: `${color}22` }} />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8E99BE" }}>Gross Profit</span>
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color, background: soft, borderRadius: 20, padding: "2px 8px" }}>
            {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {positive ? "Profitable" : "In loss"}
          </span>
        </div>
        <div style={{ fontFamily: T.fontDisplay, fontSize: 38, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginTop: 10 }}>
          ₹{fmt(Math.abs(value))}
        </div>
        <div style={{ fontSize: 12, color: "#8E99BE", marginTop: 8 }}>{sub}</div>
      </div>
    </div>
  );
}

// ── Secondary stat chip — compact, sits beside the hero ──────────────────────
function StatChip({ label, value, sub, color, Icon }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 16, padding: "14px 16px", flex: "1 1 150px", minWidth: 140 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={13} color={color} />
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkSoft }}>{label}</span>
      </div>
      <div style={{ fontFamily: T.fontMono, fontSize: 18, fontWeight: 700, color: T.ink }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Ticket-stub row — signature element for Arrivals / Due-outs ─────────────
// A dashed perforation with punched "holes" (matched to page background) is
// the one deliberate visual risk here — it borrows the travel-ticket stub
// vernacular that's specific to a booking product, rather than a generic
// table row.
function TicketRow({ guest, hotel, confNo, sale, status }) {
  const initial = (guest || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div style={{ display: "flex", alignItems: "stretch", background: T.card, border: `1px solid ${T.line}`, borderRadius: 14, overflow: "hidden", marginBottom: 10 }}>
      <div style={{ width: 40, flexShrink: 0, background: T.coralSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ width: 26, height: 26, borderRadius: "50%", background: T.coral, color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{initial}</span>
      </div>
      <div style={{ flex: 1, padding: "10px 14px", minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{guest || "—"}</div>
        <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{hotel || "—"}</div>
      </div>
      {/* perforation */}
      <div style={{ position: "relative", width: 0, flexShrink: 0 }}>
        <div style={{ position: "absolute", top: -1, left: -6, width: 12, height: 12, borderRadius: "50%", background: T.surface }} />
        <div style={{ position: "absolute", bottom: -1, left: -6, width: 12, height: 12, borderRadius: "50%", background: T.surface }} />
        <div style={{ position: "absolute", top: 6, bottom: 6, left: -1, borderLeft: `1.5px dashed ${T.line}` }} />
      </div>
      <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0, minWidth: 118 }}>
        <ConfBadge confNo={confNo} />
        <span style={{ fontFamily: T.fontMono, fontSize: 12, fontWeight: 700, color: T.ink }}>₹{fmt(sale)}</span>
        <StatusPill status={status} />
      </div>
    </div>
  );
}

function TicketPanel({ title, Icon, accent, rows, emptyText }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 18, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, background: `${accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={15} color={accent} />
        </div>
        <span style={{ fontFamily: T.fontDisplay, fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
      </div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13, color: T.inkSoft, padding: "20px 4px", textAlign: "center" }}>{emptyText}</p>
      ) : (
        <div>{rows.map((r, i) => (
          <TicketRow key={i} guest={r.guest_name} hotel={r.hotel_name} confNo={r.conf_no} sale={getEffectiveSale(r)} status={r.status} />
        ))}</div>
      )}
    </div>
  );
}

// ── Ranked leaderboard — Hot Hotels / Hot Destinations ───────────────────────
function LeaderboardPanel({ title, Icon, accent, rows, nameKey, emptyText }) {
  const max = Math.max(1, ...rows.map(r => parseFloat(r.total_sale) || 0));
  const rankColor = i => i === 0 ? T.gold : i === 1 ? "#9CA3AF" : i === 2 ? "#C08552" : T.inkSoft;
  return (
    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 18, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 9, background: `${accent}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={15} color={accent} />
        </div>
        <span style={{ fontFamily: T.fontDisplay, fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
      </div>
      {rows.length === 0 ? (
        <p style={{ fontSize: 13, color: T.inkSoft, padding: "20px 4px", textAlign: "center" }}>{emptyText}</p>
      ) : rows.slice(0, 6).map((r, i) => {
        const revenue = parseFloat(r.total_sale) || 0;
        const pct = Math.max(4, Math.round((revenue / max) * 100));
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < Math.min(rows.length, 6) - 1 ? `1px solid ${T.surface}` : "none" }}>
            <span style={{ width: 20, fontSize: 12, fontWeight: 800, color: rankColor(i), flexShrink: 0 }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r[nameKey] || "—"}</span>
                <span style={{ fontFamily: T.fontMono, fontSize: 12, fontWeight: 700, color: T.ink, flexShrink: 0 }}>₹{fmt(revenue)}</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: T.surface, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: accent }} />
              </div>
            </div>
            <span style={{ fontSize: 10, color: T.inkSoft, flexShrink: 0, width: 44, textAlign: "right" }}>{r.bookings} bk</span>
          </div>
        );
      })}
    </div>
  );
}

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = { data: null, allBookings: [], loading: true, countdown: 60 };
    this.load = this.load.bind(this);
    this._interval = null;
  }

  componentDidMount() { this.load(); this.startCountdown(); }
  componentWillUnmount() { if (this._interval) clearInterval(this._interval); }

  startCountdown() {
    if (this._interval) clearInterval(this._interval);
    this.setState({ countdown: 60 });
    this._interval = setInterval(() => {
      this.setState(prev => {
        const next = prev.countdown - 1;
        if (next <= 0) { this.load(); setTimeout(() => this.startCountdown(), 0); return { countdown: 60 }; }
        return { countdown: next };
      });
    }, 1000);
  }

  async load() {
    this.setState({ loading: true });
    try {
      const [{ data }, { data: bData }] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/bookings", { params: { limit: 500 } }),
      ]);
      this.setState({
        data:        data.data,
        allBookings: bData.data || [],
      });
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { data, loading, countdown, allBookings } = this.state;
    const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

    return (
      <div style={{ background: T.surface, margin: -24, padding: 24, minHeight: "100%" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 10 }}>
          <div>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: T.coral }}>Business Pulse</span>
            <h1 style={{ fontFamily: T.fontDisplay, fontSize: 26, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", marginTop: 2 }}>Dashboard</h1>
            <p style={{ fontSize: 13, color: T.inkSoft, marginTop: 3 }}>{today}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: T.card, border: `1px solid ${T.line}`, borderRadius: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: countdown <= 10 ? T.red : countdown <= 30 ? T.amber : T.green, animation: countdown <= 10 ? "pulse 1s infinite" : "none" }} />
              <span style={{ fontSize: 12, color: T.inkSoft, fontWeight: 600 }}>Next refresh in {countdown}s</span>
            </div>
            <button onClick={() => { this.load(); this.startCountdown(); }} disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, border: "none", background: T.ink, fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer" }}>
              <RefreshCw size={13} style={loading ? { animation: "spin 1s linear infinite" } : {}} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: T.inkSoft }}>
            <RefreshCw size={28} style={{ animation: "spin 1s linear infinite", marginBottom: 12 }} />
            <p>Loading dashboard…</p>
          </div>
        ) : data ? (
          <>
            {(() => {
              const serverStats  = data.stats || {};
              const liveSale     = allBookings.reduce((s, b) => s + getEffectiveSale(b), 0);
              const livePurchase = allBookings.reduce((s, b) => s + getEffectivePurchase(b), 0);
              const liveGP       = allBookings.reduce((s, b) => s + computeLiveGP(b), 0);
              const lossBooks    = allBookings.filter(b => computeLiveGP(b) < 0);
              const profitBooks  = allBookings.filter(b => computeLiveGP(b) >= 0);
              const lossAmt      = lossBooks.reduce((s, b)   => s + computeLiveGP(b), 0);
              const invoicedCt   = allBookings.filter(b => b.status === "Invoiced").length;
              const pendingCt    = allBookings.filter(b => b.status === "Pending Approval").length;

              return (
                <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
                  <HeroStat
                    value={liveGP}
                    positive={liveGP >= 0}
                    sub={`${profitBooks.length} profitable · ${lossBooks.length} in loss across ${allBookings.length || serverStats.total_bookings || 0} bookings`}
                  />
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: "2 1 480px" }}>
                    <StatChip label="Bookings" value={allBookings.length || serverStats.total_bookings || 0}
                      sub={`${invoicedCt} invoiced · ${pendingCt} pending`} color={T.blue} Icon={Hotel} />
                    <StatChip label="Total Sale" value={`₹${fmt(liveSale)}`} sub="all bookings" color={T.green} Icon={IndianRupee} />
                    <StatChip label="Total Purchase" value={`₹${fmt(livePurchase)}`} sub="after TAC deduction" color={T.amber} Icon={IndianRupee} />
                    <StatChip label="Total Loss" value={lossAmt < 0 ? `₹${fmt(Math.abs(lossAmt))}` : "₹0.00"} sub={`${lossBooks.length} bookings`} color={T.red} Icon={TrendingDown} />
                  </div>
                </div>
              );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
              {(() => {
                const filtered = (data.dueOuts || []).filter(r => r.status !== "Rejected");
                return <TicketPanel title={`Today's Due-Outs (${filtered.length})`} Icon={PlaneTakeoff} accent={T.coral} rows={filtered} emptyText="No check-outs today" />;
              })()}
              {(() => {
                const filtered = (data.arrivals || []).filter(r => r.status !== "Rejected");
                return <TicketPanel title={`Today's Arrivals (${filtered.length})`} Icon={PlaneLanding} accent={T.blue} rows={filtered} emptyText="No arrivals today" />;
              })()}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
              <LeaderboardPanel title="Hot Hotels" Icon={Hotel} accent={T.coral} rows={data.hotHotels || []} nameKey="hotel_name" emptyText="No data" />
              <LeaderboardPanel title="Hot Destinations" Icon={MapPin} accent={T.blue} rows={data.hotDest || []} nameKey="destination" emptyText="No data" />
            </div>

            {/* Recent Bookings — stays tabular; this is dense data, a table is the right tool */}
            <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: 18, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: `${T.ink}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FileText size={15} color={T.ink} />
                </div>
                <span style={{ fontFamily: T.fontDisplay, fontSize: 14, fontWeight: 700, color: T.ink }}>Recent Bookings</span>
              </div>
              {(data.recent || []).length === 0 ? (
                <p style={{ fontSize: 13, color: T.inkSoft, padding: "20px 4px", textAlign: "center" }}>No bookings yet</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {["#", "Date", "Guest", "Hotel", "Conf No", "Sale", "GP", "Status"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkSoft, borderBottom: `1px solid ${T.surface}`, background: T.surface, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recent || []).map((r, i) => {
                        const gp = computeLiveGP(r);
                        return (
                          <tr key={i} style={{ transition: "background 0.1s" }}
                              onMouseEnter={e => e.currentTarget.style.background = T.surface}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <td style={tdS}>#{r.id}</td>
                            <td style={tdS}>{String(r.booking_date).slice(0, 10)}</td>
                            <td style={tdS}>{r.guest_name}</td>
                            <td style={tdS}>{r.hotel_name}</td>
                            <td style={tdS}><ConfBadge confNo={r.conf_no} /></td>
                            <td style={{ ...tdS, fontFamily: T.fontMono }}>₹{fmt(getEffectiveSale(r))}</td>
                            <td style={{ ...tdS, fontFamily: T.fontMono, color: gp >= 0 ? T.green : T.red, fontWeight: 700 }}>₹{fmt(gp)}</td>
                            <td style={tdS}><StatusPill status={r.status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <p style={{ color: T.inkSoft }}>Failed to load dashboard data.</p>
        )}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      </div>
    );
  }
}

const tdS = { padding: "10px 12px", fontSize: 13, color: "#374151", borderBottom: `1px solid ${T.surface}`, verticalAlign: "middle" };

export default Dashboard;